<template>
    <v-container fluid class="pa-0">
        <template v-if="loading">
            <v-overlay :value="loading">
                <v-progress-circular
                    :size="100"
                    color="white"
                    indeterminate
                ></v-progress-circular>
            </v-overlay>
        </template>
        <template v-else>
            <banner
                :title="$t('SessionsPageTitle')"
                :description="$t('SessionsPageDescription')"
            />
            <v-container>
                <v-row justify="center">
                    <v-col cols="12" lg="6">
                        <div class="title" v-text="$t('UpcomingSessions')"></div>
                        <div class="d-flex align-center subtitle-2 py-4">
                            <v-icon class="mr-2">mdi-information</v-icon> {{ $t('CancelationPolicy') }}
                        </div>
                        <template v-if="upcomingSessions.length > 0">
                            <session-row 
                                v-for="(session, index) in upcomingSessions"
                                :key="'a'+index"
                                :photo="session.tutor.photo"
                                :date="dateFormat(session.date)"
                                :time="sessionTimeFormat(session.startTime, session.endTime)"
                                :tutorId="session.tutor.id"
                                :userName="fullName(session.tutor.firstName, session.tutor.lastName)"
                                :subject="session.bookingRequestTeachingSubject.label"
                                :message="session.bookingRequestMessage"
                                :price="session.price"
                                :files="session.files"
                                @preview-documents="getBookingFiles(session.bookingRequestId)"
                            >
                                <template v-slot:actions>
                                    <template v-if="session.bookingRequestIsCancelled">
                                        <v-chip outlined color="secondary" v-text="$t('Canceled')"></v-chip>
                                    </template>
                                    <template v-else>
                                        <div class="d-flex justify-space-around flex-sm-column">
                                            <v-btn 
                                                depressed
                                                small
                                                rounded
                                                color="primary" 
                                                class="mb-2"
                                                :to="`/${$i18n.locale}/class/virtualroom/${session.id}`"
                                                v-text="$t('JoinClassroom')"
                                                v-if="isJoiningAvailable(session.date)"
                                            ></v-btn>
                                            <v-btn 
                                                depressed
                                                small
                                                rounded
                                                color="secondary"
                                                @click="cancelSessionEventHandler(session, index)"
                                                v-text="$t('CancelSession')"
                                                v-if="isCancelAvailable(session.date)"
                                            ></v-btn>
                                            </div>
                                    </template>
                                </template>
                            </session-row>
                        </template>
                        <template v-else>
                            <v-divider></v-divider>
                            <div class="subtitle-1 pt-4" v-text="$t('NoRecords')"></div>
                        </template>
                        <div class="title py-4 mt-6" v-text="$t('PastSessions')"></div>
                        <template v-if="pastSessions.length > 0">
                            <session-row 
                                v-for="(session, index) in pastSessions"
                                :key="'b'+index"
                                :photo="session.tutor.photo"
                                :date="dateFormat(session.date)"
                                :time="sessionTimeFormat(session.startTime, session.endTime)"
                                :tutorId="session.tutor.id"
                                :userName="fullName(session.tutor.firstName, session.tutor.lastName)"
                                :subject="session.bookingRequestTeachingSubject.label"
                                :message="session.bookingRequestMessage"
                                :price="session.price"
                                :files="session.files"
                                @preview-documents="getBookingFiles(session.bookingRequestId)"
                            >
                                <template v-slot:actions>
                                    <div v-if="!session.teacherHasStarted">
                                        <v-chip outlined color="secondary">
                                            <v-icon left>mdi-eye-off</v-icon> {{ $t('MentorNoShow') }}
                                        </v-chip>
                                    </div>
                                     <div v-if="session.teacherHasStarted && !session.studentHasShown">
                                        <v-chip outlined color="secondary">
                                            <v-icon left>mdi-eye-off</v-icon> {{ $t('StudentNoShow') }}
                                        </v-chip>
                                    </div>
                                    <v-btn 
                                        depressed
                                        small
                                        rounded
                                        color="primary" 
                                        class="mb-2"
                                        v-else-if="session.teacherHasStarted && session.studentHasShown && !session.hasWroteReview"
                                        :to="`/${$i18n.locale}/student/rate-session/${session.id}`"
                                        :disabled="session.hasWroteReview"
                                        v-text="$t('RateAndReview')"
                                    ></v-btn>
                                </template>
                            </session-row>
                            <div class="text-center">
                                <v-btn 
                                    rounded
                                    depressed
                                    class="mt-12 mb-6 mx-auto"
                                    color="primary"
                                    :disabled="loadMore"
                                    :loading="loadMore"
                                    v-if="pastSessionsOffset != -9999"
                                    @click="fetchPreviousSessions"
                                >{{ $t('ShowMore') }}</v-btn>
                            </div>
                        </template>
                        <template v-else>
                            <v-divider></v-divider>                            
                            <div class="subtitle-1 pt-4" v-text="$t('NoRecords')"></div>
                        </template>                        
                    </v-col>
                </v-row>                
            </v-container>
        </template>
        <!-- Preview documents -->
        <v-dialog v-model="documentsDialog" width="620">
            <v-card>
                <v-card-title v-text="$t('Documents')"></v-card-title>
                <v-divider></v-divider>
                <div class="d-flex flex-column pa-4">
                    <template v-if="loadingDocuments">
                        <div class="text-center py-6">
                            <v-progress-circular
                                indeterminate
                                :size="50"
                                color="primary"
                            ></v-progress-circular>
                        </div>
                    </template>
                    <template v-else>
                        <v-card 
                            flat 
                            color="grey lighten-4" 
                            class="custom-radius mb-2"
                            v-for="(file, index) in bookingFiles" 
                            :key="index"
                        >
                            <div class="d-flex pa-2 pa-lg-4">
                                <v-icon left color="primary">mdi-file-outline</v-icon>
                                <a :href="`${baseImageUrl}${file.file}`" target="_blank" v-text="file.file"></a>
                            </div>
                        </v-card>
                    </template>
                </div>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-btn 
                        rounded
                        depressed
                        color="primary" 
                        class="mx-auto" 
                        v-text="$t('Close')"
                        @click="documentsDialog = false"
                    ></v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Cancel session bottom sheet -->
        <v-bottom-sheet v-model="sheet">
            <v-sheet class="text-center pa-2">
                <v-row justify="center">
                    <v-col cols="12" md="10" lg="4">
                        <div class="title" v-text="$t('CancelSessionConfirmationNotification')"></div>
                        <div class="subtitle py-4" v-text="$t('CancelSessionConfirmationClarification')"></div>
                        <v-btn
                            text
                            color="red"
                            @click="cancelSession"
                            v-text="$t('Yes')"
                        ></v-btn>
                        <v-btn
                            text
                            color="primary"
                            @click="sheet = !sheet, selectedSessionToCancel = {}"
                            v-text="$t('No')"
                        ></v-btn>
                    </v-col>
                </v-row>
            </v-sheet>
        </v-bottom-sheet>
    </v-container>
</template>

<script>
    import { mapGetters } from 'vuex'
    import moment from 'moment'
    import Banner from '@/components/pageBanner'
    import SessionRow from '@/components/sessionItem'

    export default {
        name: 'studentSessions',
        data: () => ({
            sessions: [],
            upcomingSessions: [],
            pastSessions: [],
            pastSessionsOffset: 0,
            selectedSessionToCancel: {},
            bookingFiles: [],
            sessionTimeParameter: null,
            sheet: false,
            snackbar: {
                show: true,
                message: ''
            },
            documentsDialog: false,
            loadingDocuments: true,
            loadMore: false,
            loading: true
        }),
        components: {
            Banner,
            SessionRow
        },
        computed: {
            ...mapGetters(['user', 'baseImageUrl', 'preferedLanguage', 'defaultLanguage']),
        },
        mounted() {
            this.fetchSessions()
            this.fetchPreviousSessions()
            this.fetchTimeParameter()
        },
        methods: {
            async fetchSessions() {
                await this.axios.get('/Booking/BookedSessions', {
                    params: {
                        studentId: this.user.id,
                        languageId: this.preferedLanguage
                    }
                })
                .then(response => {
                    this.upcomingSessions = response.data.upcomingBookingRequests
                    this.loading = false
                }).catch(error => { 
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.loading = false
                })
            },
            async fetchPreviousSessions(){
                this.loadMore = true
                await this.axios.get('/Booking/GetStudentPreviousBookedSessions', {
                    params: {
                        studentId: this.user.id,
                        languageId: this.preferedLanguage,
                        offset: this.pastSessionsOffset,
                        amount: 5
                    }
                })
                .then(response => {
                    this.pastSessions = this.pastSessions.concat(response.data.list)
                    this.pastSessionsOffset = response.data.offset
                    this.loadMore = false
                }).catch(error => { 
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.loadMore = false
                })
            },
            async fetchTimeParameter() {
                const { data } = await this.axios.get(`ConfigParameter/GetCancelTimeParameter?languageId=${this.defaultLanguage.id}`)
                this.sessionTimeParameter = data
            },
            dateFormat(date) {
                return moment(date).format("dddd, MM/DD/YYYY")
            },
            sessionTimeFormat(startTime, endTime) {
                return moment(startTime, 'HH:mm:ss').format("LT") + ' - ' + moment(endTime, 'HH:mm:ss').format("LT")
            },
            fullName(firstName, lastName) {
                return `${firstName} ${lastName}`
            },
            cancelSessionEventHandler(session, index) {
                this.sheet = true
                this.selectedSessionToCancel = {
                    index: index,
                    id: session.bookingRequestId,
                    studentId: session.bookingRequestStudentId,
                    timeslotId: session.id
                }
            },
            cancelSession() {
                if(this.selectedSessionToCancel.studentId != this.user.id) {
                    const snackbar = {
                        show: true,
                        message: this.$i18n.t('Unauthorized')
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    return
                }

                this.axios.post(`/Booking/CancelBookingRequest?id=${this.selectedSessionToCancel.id}&studentId=${this.selectedSessionToCancel.studentId}&timeslotId=${this.selectedSessionToCancel.timeslotId}`)
                .then(response => {
                    if(response.status == 200) {
                        this.upcomingSessions[this.selectedSessionToCancel.index].bookingRequestIsCancelled = true
                        this.sheet = false
                        const snackbar = {
                            show: true,
                            message: this.$i18n.t('SessionCanceledNotification')
                        }
                        this.$EventBus.$emit('snackbar', snackbar)
                    }
                })
                .catch(error => { 
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
            },
            getBookingFiles(id) {
                this.loadingDocuments = true
                this.documentsDialog = true
                this.axios.get(`/Booking/GetBookingRequestFiles?id=${id}`)
                .then(response => {
                    this.bookingFiles = response.data.list
                    this.loadingDocuments = false
                })
                .catch(error => {
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.loadingDocuments = false
                    this.documentsDialog = false
                })
            },
            isJoiningAvailable(date) {
                let sessionDate = moment(date)
                let now = moment()
                let sessionDateWithParameter = sessionDate.subtract({hours: this.sessionTimeParameter})
                return sessionDateWithParameter.isBefore(now) && sessionDate.isBefore(now)
            },
            isCancelAvailable(date) {
                let sessionDate = moment(date)
                let now = moment()
                let sessionDateWithParameter = sessionDate.subtract({hours: this.sessionTimeParameter})
                return now.isBefore(sessionDateWithParameter)
            }
        }
    }
</script>

<style lang="scss" scoped>
.v-list-item{
    border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>